import { Modal } from '../../../../../components/Modal/Modal'
import React, { useEffect, useMemo, useState } from 'react'
import { Room, RoomSession } from '../../../../../models/Room'
import { formatDateHM, formatTimeHMS } from '../../common/aifeatures/utils'
import './TrimMeetingModal.scss'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useTrimMeetingMutation } from '../../../../../store/media/media'
import { MessagePanel } from '../../../../../components/MessagePanel/MessagePanel'
import { StartEndPicker } from '../../../../../components/StartEndPicker/StartEndPicker'
import { getFormattedTimeInterval } from '../../../../../common/duration'
import { TrimMeetingLoading } from './TrimMeetingLoading'

export interface TrimMeetingModalProps {
  room: Room | null
  roomSession: RoomSession
  show: boolean
  onTrimmingCompleted: () => void
  isPollingForAllocatedTranscripts: boolean
  onHide: () => void
}

export const TrimMeetingModal: React.FC<TrimMeetingModalProps> = ({
  room,
  roomSession,
  show,
  onTrimmingCompleted,
  isPollingForAllocatedTranscripts,
  onHide,
}) => {
  const [desiredStart, setDesiredStart] = useState(0)
  const [desiredEnd, setDesiredEnd] = useState(0)
  const [overtime, setOvertime] = useState<number | null>(null)
  const [trimmedFromStartInSeconds, setTrimmedFromStartInSeconds] = useState(0)
  const [trimmedFromEndInSeconds, setTrimmedFromEndInSeconds] = useState(0)
  const accountId = useAppSelector(selectAccountId)

  const [joinWindow] = room?.config.joinWindows ?? []
  const scheduledEnd = joinWindow?.end

  const originalLengthInSeconds: number | null = useMemo(() => {
    if (!roomSession.finishedAt) {
      return null
    }
    return (new Date(roomSession.finishedAt).getTime() - new Date(roomSession.startedAt).getTime()) / 1000
  }, [roomSession])

  const [trimMeeting, { isLoading: isTrimMeetingLoading }] = useTrimMeetingMutation()
  const submitTrimmingRequest = async () => {
    if (!room || !roomSession) {
      return
    }
    const result = await trimMeeting({
      start: trimmedFromStartInSeconds,
      roomId: room.id,
      roomSessionId: roomSession.id,
      accountId,
      duration: desiredEnd - desiredStart,
    })
    if (!result.error) {
      onTrimmingCompleted()
    }
  }

  useEffect(() => {
    if (joinWindow == null) {
      return
    }

    if (originalLengthInSeconds && joinWindow.start && joinWindow.end) {
      const scheduledMeetingLengthInSeconds = new Date(joinWindow.end).getTime() / 1000 - new Date(joinWindow.start).getTime() / 1000

      if (scheduledMeetingLengthInSeconds < originalLengthInSeconds) {
        setOvertime(originalLengthInSeconds - scheduledMeetingLengthInSeconds)
      } else {
        setOvertime(null)
      }
    } else {
      setOvertime(null)
    }
  }, [joinWindow, originalLengthInSeconds])

  const sessionStartedAt = new Date(roomSession.startedAt).getTime() / 1000
  const sessionFinishedAt = roomSession.finishedAt ? new Date(roomSession.finishedAt).getTime() / 1000 : null

  const handleStartEndChanged = (start: number, end: number) => {
    setDesiredStart(start)
    setDesiredEnd(end)
    setTrimmedFromStartInSeconds(start - sessionStartedAt)
    if (sessionFinishedAt) {
      setTrimmedFromEndInSeconds(sessionFinishedAt - end)
    }
  }

  if (!originalLengthInSeconds) {
    return null
  }
  const body =
    isTrimMeetingLoading || isPollingForAllocatedTranscripts ? (
      <TrimMeetingLoading />
    ) : (
      <div className='trim-meeting-container d-flex flex-column'>
        {roomSession.finishedAt && scheduledEnd && overtime ? (
          <span className='paragraph-medium mb-2'>
            This meeting was scheduled to end at {formatDateHM(scheduledEnd)} but continued until {formatDateHM(roomSession.finishedAt)}. You can remove the
            extra time to create cleaner, more focused meeting insights.
          </span>
        ) : (
          <p className='paragraph-medium mb-0'>You can remove the extra time to create cleaner, more focused meeting insights.</p>
        )}
        <MessagePanel
          variant='warning'
          message={
            <>
              The trimmed portion will be permanently removed from the meeting and <b>can’t be restored later.</b>
            </>
          }
        />
        <div>
          <div className='mb-3 fw-bold'>Change meeting duration</div>
          {sessionStartedAt && sessionFinishedAt && (
            <StartEndPicker
              minEpochInSeconds={sessionStartedAt}
              maxEpochInSeconds={sessionFinishedAt}
              onStartEndChanged={handleStartEndChanged}
              disabled={isTrimMeetingLoading || isPollingForAllocatedTranscripts}
            />
          )}
        </div>
        <div>
          <div className='trim-info-lines'>
            {trimmedFromStartInSeconds > 0 || trimmedFromEndInSeconds ? (
              <p className='mb-1'>
                Original length: {originalLengthInSeconds ? formatTimeHMS(originalLengthInSeconds) : 'N/A'} &rarr; Trimmed length:{' '}
                {formatTimeHMS(desiredEnd - desiredStart)}
              </p>
            ) : (
              <>Length not changed</>
            )}
            {trimmedFromStartInSeconds > 0 && (
              <p className='mb-1'>
                Removing <b>{getFormattedTimeInterval(trimmedFromStartInSeconds, false)}</b> from the start.
              </p>
            )}
            {trimmedFromEndInSeconds > 0 && (
              <p className='mb-1'>
                Removing <b>{getFormattedTimeInterval(trimmedFromEndInSeconds, false)}</b> from the end.
              </p>
            )}
          </div>
        </div>
      </div>
    )
  return (
    <Modal
      size='lg'
      show={show}
      onHide={onHide}
      header={<h5>Trim meeting</h5>}
      headerBodySeparator={true}
      body={body}
      dialogClassName='trim-meeting-modal'
      showFooterActions={true}
      onFooterPrimaryAction={submitTrimmingRequest}
      footerSecondaryActionDisabled={isTrimMeetingLoading || isPollingForAllocatedTranscripts}
      footerPrimaryActionDisabled={
        (trimmedFromStartInSeconds === 0 && trimmedFromEndInSeconds === 0) || isTrimMeetingLoading || isPollingForAllocatedTranscripts
      }
      footerPrimaryActionTitle='Trim meeting'
      footerPrimaryActionButtonVariant='danger'
      footerClassName='pt-0'
    />
  )
}
